// Google Analytics 4 — env-driven. GA4 loads and tracks only when a Measurement ID is
// present; when it's blank (local dev, preview, unconfigured deploys) every function here is
// a no-op and no gtag script or cookies are added.
//
// The ID is read at RUNTIME from window.__ENV__.GA_MEASUREMENT_ID (injected by /env.js, which
// nginx regenerates from the container's GA_MEASUREMENT_ID env var on start — see Dockerfile).
// This lets one built image be pointed at any GA property without a rebuild. The build-time
// VITE_GA_MEASUREMENT_ID is kept only as a fallback for local `vite build` via .env.
const runtimeId =
  typeof window !== 'undefined' ? window.__ENV__?.GA_MEASUREMENT_ID : undefined;
const MEASUREMENT_ID =
  runtimeId || (import.meta.env.VITE_GA_MEASUREMENT_ID as string | undefined) || undefined;

export const analyticsEnabled = Boolean(MEASUREMENT_ID);

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    __ENV__?: { GA_MEASUREMENT_ID?: string; API_BASE_URL?: string };
  }
}

let initialized = false;

/** Inject gtag.js and configure GA4. Safe no-op unless an ID is set (runs once). */
export function initAnalytics(): void {
  if (!MEASUREMENT_ID || initialized || typeof window === 'undefined') return;
  initialized = true;

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`;
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer || [];
  // gtag pushes its raw `arguments` object onto the dataLayer — keep this signature.
  window.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer!.push(arguments);
  };
  window.gtag('js', new Date());
  // send_page_view:false — this is a client-routed SPA, so we emit page_view ourselves on
  // every route change (see trackPageView) to avoid miscounting and double-counting the load.
  window.gtag('config', MEASUREMENT_ID, { send_page_view: false });
}

/** Record a page view for an in-app route change. No-op when analytics is disabled. */
export function trackPageView(path: string, title?: string): void {
  if (!MEASUREMENT_ID || typeof window === 'undefined' || !window.gtag) return;
  window.gtag('event', 'page_view', {
    page_path: path,
    page_title: title,
    page_location: window.location.origin + path,
  });
}

/** Record a custom GA4 event (e.g. a conversion). No-op when analytics is disabled. */
export function trackEvent(name: string, params?: Record<string, unknown>): void {
  if (!MEASUREMENT_ID || typeof window === 'undefined' || !window.gtag) return;
  window.gtag('event', name, params);
}
